import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from 'Core/components/FormElements/Card';
import { Alert, AlertError } from 'Core/components/Alert';
import { PrimaryButton } from 'Core/components/Button';
import Consent from 'Modules/checkout/components/Consent';
import SaveMethod from 'Modules/checkout/components/SaveMethod';
import PayNow from 'Modules/checkout/components/PayNow';
import AuthenticatePayment from 'Modules/checkout/components/AuthenticatePayment';

const STEPS = {
  MAKE_PAYMENT: {
    title: 'Rechnung jetzt bezahlen',
    text: 'Die Eingabe wird vorbereitet. Bitte laden Sie die Seite neu.',
  },
  APPLICATION_REQUIRED: {
    title: 'Ratenkauf mit easyCredit',
    text: 'Ihre Finanzierungsanfrage wird vorbereitet. Bitte laden Sie die Seite neu.',
  },
  CONSENT_REQUIRED: {
    title: 'Bitte setzen Sie Ihren Antrag bei easyCredit fort.',
    text: '',
  },
  OPEN: {
    title: 'Bitte setzen Sie Ihren Antrag bei easyCredit fort.',
    text: '',
  },
  COMPLETE_APPLICATION: {
    title: 'Finanzierung vorläufig genehmigt',
    text: 'Bitte bestätigen Sie Ihre Finanzierung bei easyCredit.',
  },
  NO_ACTION_ALREADY_APPROVED: {
    title: 'Finanzierung genehmigt',
    text: 'Ihre Finanzierung mit easyCredit ist genehmigt. Es ist nichts weiter zu tun.',
  },
  DECLINED: {
    title: 'Finanzierung abgelehnt',
    text: 'easyCredit hat die Finanzierung leider abgelehnt. Sie können eine andere Zahlungsart wählen.',
  },
  REFUND: {
    title: 'Rückerstattung',
    text: 'Für diese Finanzierung wurde eine Rückerstattung veranlasst.',
  },
  ERROR: {
    title: 'Ein Fehler ist aufgetreten',
    text: 'Die Finanzierungsanfrage konnte nicht verarbeitet werden. Sie können eine andere Zahlungsart wählen.',
  },
  COMPLETED: {
    title: 'Zahlung abgeschlossen',
    text: 'Vielen Dank. Diese Zahlung ist abgeschlossen.',
  },
  CANCELLED: { title: 'Zahlung storniert', text: 'Diese Zahlungsanforderung wurde storniert.' },
  EXPIRED: { title: 'Zahlung abgelaufen', text: 'Diese Zahlungsanforderung ist abgelaufen.' },
  APPROVE_FUTURE_PAYMENT: {
    title: 'Zahlungsart hinterlegen',
    text: 'Die Eingabe wird vorbereitet. Bitte laden Sie die Seite neu.',
  },
  PAYMENT_METHOD_PROCESSING: {
    title: 'Ihre Angaben werden geprüft',
    text: 'Das kann einen Moment dauern. Sie müssen nichts weiter tun.',
  },
  PAYMENT_PROCESSING: {
    title: 'Zahlung wird verarbeitet',
    text: 'Ihre Zahlung ist unterwegs. Das kann einige Tage dauern.',
  },
  AUTHENTICATE_PAYMENT: {
    title: 'Bitte bestätigen Sie die Zahlung.',
    text: 'Ihre Bank möchte die Zahlung von Ihnen bestätigen lassen.',
  },
  NO_ACTION_PAID: {
    title: 'Zahlung erhalten',
    text: 'Vielen Dank. Ihre Zahlung ist eingegangen.',
  },
  PAYMENT_FAILED: {
    title: 'Zahlung fehlgeschlagen',
    text: 'Die Zahlung konnte nicht durchgeführt werden. Bitte wählen Sie eine andere Zahlungsart.',
  },
  PAY_BY_INVOICE: {
    title: 'Zahlung auf Rechnung',
    text: 'Die Rechnung für diese Bestellung wird Ihnen zugesandt. Bitte überweisen Sie den Betrag nach Erhalt. Sie können die Zahlungsart hier jederzeit ändern.',
  },
};

const METHOD_STEPS = {
  CARD: {
    MAKE_PAYMENT: {
      title: 'Rechnung jetzt mit Kreditkarte bezahlen',
      text: 'Der Rechnungsbetrag wird sofort von Ihrer Karte abgebucht.',
    },
    APPROVE_FUTURE_PAYMENT: {
      title: 'Kreditkarte hinterlegen',
      text: 'Ihre Karte wird erst bei Rechnungsstellung belastet.',
    },
    NO_ACTION_METHOD_SAVED: {
      title: 'Ihre Kreditkarte ist hinterlegt.',
      text: 'Der Rechnungsbetrag wird erst bei Rechnungsstellung abgebucht.',
    },
  },
  SEPA: {
    MAKE_PAYMENT: {
      title: 'Rechnung jetzt per Lastschrift bezahlen',
      text: 'Der Rechnungsbetrag wird von Ihrem Konto abgebucht. Die Gutschrift kann einige Tage dauern.',
    },
    APPROVE_FUTURE_PAYMENT: {
      title: 'Lastschriftmandat erteilen',
      text: 'Der Rechnungsbetrag wird erst bei Rechnungsstellung von Ihrem Konto abgebucht.',
    },
    NO_ACTION_METHOD_SAVED: {
      title: 'Ihr Lastschriftmandat ist erteilt.',
      text: 'Der Rechnungsbetrag wird erst bei Rechnungsstellung abgebucht.',
    },
  },
};

export const stepOf = (action, method) => {
  const specific = METHOD_STEPS[method]?.[action];
  if (specific) {
    return { ...specific, key: `checkout.next.${action}.${method}` };
  }
  return { ...(STEPS[action] || { title: action, text: '' }), key: `checkout.next.${action}` };
};

const REDIRECTING = ['CONSENT_REQUIRED', 'COMPLETE_APPLICATION', 'OPEN'];

export default function NextStep({
  next,
  session,
  consentMutation,
  redirectNow = false,
  onRefresh = () => null,
}) {
  const { t } = useTranslation();
  const action = next.next_action;
  const redirectLink = REDIRECTING.includes(action) ? next.redirect_link || null : null;

  useEffect(() => {
    if (redirectLink && redirectNow) {
      window.location.assign(redirectLink);
    }
  }, [redirectLink, redirectNow]);

  if (action === 'CONSENT_REQUIRED' && !redirectLink) {
    return <Consent mutation={consentMutation} />;
  }

  const method = session.selected_payment_method;
  const ready = Boolean(next.client_secret && next.publishable_key);
  const canSave = action === 'APPROVE_FUTURE_PAYMENT' && ready;
  const canPay = action === 'MAKE_PAYMENT' && ready;
  const preparing = (action === 'APPROVE_FUTURE_PAYMENT' || action === 'MAKE_PAYMENT') && !ready;
  const step = stepOf(action, preparing ? null : method?.method);
  const canAuthenticate = action === 'AUTHENTICATE_PAYMENT' && ready;
  const text = (canSave || canPay) && !METHOD_STEPS[method?.method] ? '' : step.text;
  const invoiceNo = session.payment?.sales_invoice_no;
  const receiptUrl =
    action === 'NO_ACTION_PAID' && next.receipt_url?.startsWith('https://')
      ? next.receipt_url
      : null;
  return (
    <Card className="border-solid border-gray-300 shadow-md" data-testid={`next-${action}`}>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{t(`${step.key}.title`, step.title)}</h3>
      {canPay && invoiceNo && (
        <p className="text-sm text-gray-600 mb-3" data-testid="pay-now-invoice">
          {t('checkout.payNow.invoice', 'Rechnung')} {invoiceNo}
        </p>
      )}
      {method?.lock === 'LOCKED' && (
        <p className="text-sm text-gray-600 mb-3">
          {t('checkout.lockedBySales', 'Diese Zahlungsart wurde von Ihrem Berater festgelegt.')}
        </p>
      )}
      {text && <Alert type="infoLight">{t(`${step.key}.text`, text)}</Alert>}
      {canPay && next.failure_code && (
        <div className="mt-3" data-testid="last-attempt-failed">
          <AlertError>
            {t('checkout.payNow.declined', 'Der letzte Zahlungsversuch wurde abgelehnt. Bitte versuchen Sie es mit einer anderen Karte.')}
          </AlertError>
        </div>
      )}
      {canPay && (
        <PayNow
          method={method?.method}
          customer={session.customer}
          clientSecret={next.client_secret}
          publishableKey={next.publishable_key}
          onDone={onRefresh}
        />
      )}
      {canSave && (
        <SaveMethod
          method={method?.method}
          customer={session.customer}
          clientSecret={next.client_secret}
          publishableKey={next.publishable_key}
          onDone={onRefresh}
        />
      )}
      {canAuthenticate && (
        <AuthenticatePayment
          clientSecret={next.client_secret}
          publishableKey={next.publishable_key}
          onDone={onRefresh}
        />
      )}
      {receiptUrl && (
        <div className="flex justify-end mt-2">
          <a
            href={receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="receipt-link"
            className="text-sm font-semibold underline"
          >
            {t('checkout.next.receipt', 'Beleg ansehen und herunterladen')}
          </a>
        </div>
      )}
      {redirectLink && (
        <div className="flex justify-end mt-2">
          <PrimaryButton
            type="button"
            data-testid="go-easycredit"
            onClick={() => window.location.assign(redirectLink)}
          >
            {t('checkout.next.continueEasyCredit', 'Weiter zu easyCredit')}
          </PrimaryButton>
        </div>
      )}
    </Card>
  );
}
